/**
 * Guest API client — Section 10
 */

import { saveProject } from './projects';
import { convertGuestLyrics } from '@/lyrics/conversion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Create a guest session. Returns { session_token }. */
export async function startGuestSession(eventName = null) {
  const query = eventName ? `?event=${encodeURIComponent(eventName)}` : '';
  const res = await fetch(`${BACKEND_URL}/api/v1/auth/guest/start${query}`, { method: 'POST' });
  if (!res.ok) throw new Error(`Guest session creation failed: ${res.status}`);
  return res.json();
}

/** Save guest arrangement data. */
export async function saveGuestArrangement(sessionToken, data) {
  const res = await fetch(`${BACKEND_URL}/api/v1/guest/save`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Guest save failed: ${res.status}`);
  return res.json();
}

export function guestSaveBody(arrangementData) {
  return { arrangement_data: arrangementData };
}

/** Retrieve the last server-acknowledged guest arrangement after a reload. */
export async function loadGuestArrangement(sessionToken) {
  const res = await fetch(`${BACKEND_URL}/api/v1/guest/session`, {
    headers: { 'Authorization': `Bearer ${sessionToken}` },
  });
  if (!res.ok) throw new Error(`Guest session load failed: ${res.status}`);
  return res.json();
}

/** Export guest beat — captures email, triggers MP3 render + email delivery. */
export async function exportGuestBeat(sessionToken, email, mp3Blob, attemptKey, beatName) {
  const form = new FormData();
  form.append('email', email);
  form.append('attempt_key', attemptKey);
  form.append('beat_name', beatName || 'Workshop Beat');
  form.append('mp3', mp3Blob, 'my-beat.mp3');
  const res = await fetch(`${BACKEND_URL}/api/v1/guest/export`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${sessionToken}`,
    },
    body: form,
  });
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new Error(payload?.detail || `Guest export failed: ${res.status}`);
  }
  return res.json();
}

/** Convert guest session to full account. */
export async function convertGuestSession(sessionToken, email, password, displayName) {
  const res = await fetch(`${BACKEND_URL}/api/v1/guest/convert`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sessionToken}`,
    },
    body: JSON.stringify(guestConvertBody(email, password, displayName)),
  });
  if (!res.ok) throw new Error(`Guest conversion failed: ${res.status}`);
  return res.json();
}

/** The conversion request deliberately contains account fields only; lyrics stay on-device. */
export function guestConvertBody(email, password, displayName) {
  return { email, password, display_name: displayName };
}

export async function convertGuestAccountWithLyrics({
  sessionToken,
  email,
  password,
  displayName,
  guestSessionId,
  lyrics,
  kiosk = false,
  storage = localStorage,
}) {
  return convertGuestLyrics({
    guestSessionId,
    lyrics,
    kiosk,
    storage,
    convert: () => convertGuestSession(sessionToken, email, password, displayName),
    save: saveProject,
    setToken: (token) => storage.setItem('ltbb-ba-token', token),
  });
}
